import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { api, fmtErr } from "../lib/api";
import { INDUSTRIES, ICONS, MODULES, DEFAULT_STAGES, ROLES } from "../lib/constants";
import { Lightning, Check, Plus, Trash, DotsSixVertical, ArrowRight, ArrowLeft } from "@phosphor-icons/react";
import { Switch } from "../components/ui/switch";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { toast } from "sonner";

const STEPS = ["Industries", "Modules", "Workflow", "Team"];

export default function Onboarding() {
  const { user, refreshOrg } = useAuth();
  const nav = useNavigate();
  const [step, setStep] = useState(0);
  const [busy, setBusy] = useState(false);
  const [industries, setIndustries] = useState([]);
  const [modules, setModules] = useState(MODULES.filter((m) => m.default).map((m) => m.key));
  const [stages, setStages] = useState(DEFAULT_STAGES.map((s) => ({ ...s })));
  const [team, setTeam] = useState([]);
  const [dragIdx, setDragIdx] = useState(null);

  const toggleIndustry = (key) =>
    setIndustries((v) => (v.includes(key) ? v.filter((k) => k !== key) : [...v, key]));

  const complete = async () => {
    setBusy(true);
    try {
      await api.post("/org/onboarding", {
        industries, enabled_modules: modules,
        workflow_stages: stages, team: team.filter((t) => t.email),
      });
      await refreshOrg();
      toast.success("Workspace configured — welcome to Formigen");
      nav("/dashboard");
    } catch (e) {
      toast.error(fmtErr(e));
    } finally { setBusy(false); }
  };

  const onDropStage = (idx) => {
    if (dragIdx === null || dragIdx === idx) return;
    setStages((s) => {
      const next = [...s];
      const [moved] = next.splice(dragIdx, 1);
      next.splice(idx, 0, moved);
      return next;
    });
    setDragIdx(null);
  };

  return (
    <div className="min-h-screen bg-[var(--c-bg)]" data-testid="onboarding-page">
      <div className="max-w-5xl mx-auto px-6 py-10">
        <div className="flex items-center gap-2.5 mb-10">
          <div className="w-7 h-7 bg-[var(--c-accent)] rounded-sm flex items-center justify-center">
            <Lightning size={16} weight="fill" color="var(--c-text)" />
          </div>
          <span className="text-sm font-semibold text-[var(--c-text)]">Formigen</span>
          <span className="text-[10px] font-mono tracking-widest text-[var(--c-muted)] uppercase ml-2">Workspace Setup · {user?.org?.name}</span>
        </div>

        <div className="flex items-center gap-0 mb-10" data-testid="onboarding-steps">
          {STEPS.map((s, i) => (
            <div key={s} className="flex items-center">
              <div className="flex items-center gap-2">
                <div className={`w-6 h-6 rounded-sm flex items-center justify-center text-[11px] font-mono border ${
                  i < step ? "bg-[var(--c-green)] border-[var(--c-green)] text-[var(--c-text)]" :
                  i === step ? "bg-[var(--c-accent)] border-[var(--c-accent)] text-[var(--c-text)]" :
                  "border-[var(--c-border)] text-[var(--c-muted)]"}`}>
                  {i < step ? <Check size={12} weight="bold" /> : i + 1}
                </div>
                <span className={`text-xs ${i === step ? "text-[var(--c-text)]" : "text-[var(--c-muted)]"}`}>{s}</span>
              </div>
              {i < STEPS.length - 1 && <div className="w-10 md:w-16 h-px bg-[var(--c-border)] mx-3" />}
            </div>
          ))}
        </div>

        {step === 0 && (
          <div data-testid="onboarding-step-industries">
            <h2 className="text-2xl tracking-tight font-medium text-[var(--c-text)]">What does your organization manufacture?</h2>
            <p className="text-sm text-[var(--c-muted)] mt-2">Select all that apply. Each industry loads its own spec schema, calculations, validations, drawing types and BOM template.</p>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3 mt-8">
              {INDUSTRIES.map((ind) => {
                const Icon = ICONS[ind.icon];
                const active = industries.includes(ind.key);
                return (
                  <button
                    key={ind.key}
                    data-testid={`industry-card-${ind.key}`}
                    onClick={() => toggleIndustry(ind.key)}
                    className={`relative text-left p-3.5 rounded-sm border bg-[var(--c-surface)] transition-colors duration-150 min-h-[110px] ${
                      active ? "border-[var(--c-accent)]" : "border-[var(--c-border)] hover:border-[var(--c-muted)]"}`}
                  >
                    {active && (
                      <div className="absolute top-2 right-2 w-4 h-4 bg-[var(--c-accent)] rounded-sm flex items-center justify-center">
                        <Check size={10} weight="bold" color="var(--c-text)" />
                      </div>
                    )}
                    <Icon size={20} weight="fill" style={{ color: ind.color }} />
                    <div className="text-xs font-medium text-[var(--c-text)] mt-2.5 leading-snug">{ind.name}</div>
                    <div className="text-[10px] text-[var(--c-muted)] mt-1 leading-snug">{ind.desc}</div>
                  </button>
                );
              })}
            </div>
          </div>
        )}

        {step === 1 && (
          <div data-testid="onboarding-step-modules">
            <h2 className="text-2xl tracking-tight font-medium text-[var(--c-text)]">Select your modules</h2>
            <p className="text-sm text-[var(--c-muted)] mt-2">The sidebar and tooling adapt to what you enable. You can change this later in Settings.</p>
            <div className="mt-8 border border-[var(--c-border)] rounded-sm divide-y divide-[var(--c-border)] bg-[var(--c-surface)]">
              {MODULES.map((m) => {
                const on = modules.includes(m.key);
                return (
                  <div key={m.key} className="flex items-center gap-4 px-5 py-4" data-testid={`module-row-${m.key}`}>
                    <div className="flex-1">
                      <div className="text-sm font-medium text-[var(--c-text)]">{m.label}</div>
                      <div className="text-xs text-[var(--c-muted)] mt-0.5">{m.desc}</div>
                    </div>
                    <Switch
                      data-testid={`module-switch-${m.key}`}
                      checked={on}
                      onCheckedChange={(v) => setModules((ms) => (v ? [...ms, m.key] : ms.filter((k) => k !== m.key)))}
                    />
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {step === 2 && (
          <div data-testid="onboarding-step-workflow">
            <h2 className="text-2xl tracking-tight font-medium text-[var(--c-text)]">Configure your workflow</h2>
            <p className="text-sm text-[var(--c-muted)] mt-2">Drag to reorder. Rename stages, set SLA target days, remove optional stages or add your own. Intake and Released are fixed.</p>
            <div className="mt-8 space-y-2">
              {stages.map((s, i) => (
                <div
                  key={s.key}
                  data-testid={`stage-row-${s.key}`}
                  draggable
                  onDragStart={() => setDragIdx(i)}
                  onDragOver={(e) => e.preventDefault()}
                  onDrop={() => onDropStage(i)}
                  className={`flex items-center gap-3 px-4 py-3 bg-[var(--c-surface)] border rounded-sm transition-colors duration-150 ${
                    dragIdx === i ? "border-[var(--c-accent)]" : "border-[var(--c-border)]"}`}
                >
                  <DotsSixVertical size={16} className="text-[var(--c-muted)] cursor-grab shrink-0" />
                  <span className="font-mono text-[10px] text-[var(--c-muted)] w-6">{String(i + 1).padStart(2, "0")}</span>
                  <Input
                    data-testid={`stage-name-${s.key}`}
                    value={s.name}
                    disabled={s.locked}
                    onChange={(e) => setStages((ss) => ss.map((x, xi) => (xi === i ? { ...x, name: e.target.value } : x)))}
                    className="bg-[var(--c-bg)] border-[var(--c-border)] text-[var(--c-text)] h-8 text-sm max-w-56"
                  />
                  <div className="flex items-center gap-2 ml-auto">
                    <span className="text-[10px] font-mono tracking-wider text-[var(--c-muted)] uppercase">SLA days</span>
                    <Input
                      data-testid={`stage-sla-${s.key}`}
                      type="number" min={0} value={s.sla_days}
                      onChange={(e) => setStages((ss) => ss.map((x, xi) => (xi === i ? { ...x, sla_days: e.target.value } : x)))}
                      className="bg-[var(--c-bg)] border-[var(--c-border)] text-[var(--c-text)] h-8 text-sm w-20 font-mono"
                    />
                    {!s.locked ? (
                      <button data-testid={`stage-remove-${s.key}`} onClick={() => setStages((ss) => ss.filter((_, xi) => xi !== i))}
                        className="p-1.5 text-[var(--c-muted)] hover:text-[var(--c-red)] transition-colors duration-150">
                        <Trash size={14} />
                      </button>
                    ) : (
                      <span className="text-[10px] font-mono text-[var(--c-muted)] px-1.5 uppercase tracking-wider">Fixed</span>
                    )}
                  </div>
                </div>
              ))}
            </div>
            <Button
              data-testid="add-stage-btn"
              variant="outline"
              className="mt-4 border-[var(--c-border)] text-[var(--c-muted)] hover:text-[var(--c-text)] hover:bg-[var(--c-surface-2)] rounded-sm"
              onClick={() => setStages((ss) => [...ss.slice(0, -1), { key: `custom_${Date.now()}`, name: "Custom Stage", sla_days: 2, locked: false }, ss[ss.length - 1]])}
            >
              <Plus size={14} className="mr-1.5" /> Add custom stage
            </Button>
          </div>
        )}

        {step === 3 && (
          <div data-testid="onboarding-step-team">
            <h2 className="text-2xl tracking-tight font-medium text-[var(--c-text)]">Invite your team</h2>
            <p className="text-sm text-[var(--c-muted)] mt-2">Members get an account with a default password they can change later. You can also do this from Settings.</p>
            <div className="mt-8 space-y-2">
              {team.map((t, i) => (
                <div key={i} className="flex items-center gap-2" data-testid={`team-row-${i}`}>
                  <Input data-testid={`team-name-${i}`} placeholder="Name" value={t.name}
                    onChange={(e) => setTeam((ts) => ts.map((x, xi) => (xi === i ? { ...x, name: e.target.value } : x)))}
                    className="bg-[var(--c-surface)] border-[var(--c-border)] text-[var(--c-text)] h-9 text-sm" />
                  <Input data-testid={`team-email-${i}`} placeholder="Email" type="email" value={t.email}
                    onChange={(e) => setTeam((ts) => ts.map((x, xi) => (xi === i ? { ...x, email: e.target.value } : x)))}
                    className="bg-[var(--c-surface)] border-[var(--c-border)] text-[var(--c-text)] h-9 text-sm" />
                  <Select value={t.role} onValueChange={(v) => setTeam((ts) => ts.map((x, xi) => (xi === i ? { ...x, role: v } : x)))}>
                    <SelectTrigger data-testid={`team-role-${i}`} className="bg-[var(--c-surface)] border-[var(--c-border)] text-[var(--c-text)] h-9 text-sm w-52">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-[var(--c-surface)] border-[var(--c-border)]">
                      {ROLES.map((r) => <SelectItem key={r.key} value={r.key} className="text-[var(--c-text)] focus:bg-[var(--c-surface-2)]">{r.label}</SelectItem>)}
                    </SelectContent>
                  </Select>
                  <button data-testid={`team-remove-${i}`} onClick={() => setTeam((ts) => ts.filter((_, xi) => xi !== i))}
                    className="p-2 text-[var(--c-muted)] hover:text-[var(--c-red)] transition-colors duration-150">
                    <Trash size={14} />
                  </button>
                </div>
              ))}
            </div>
            <Button data-testid="add-team-member-btn" variant="outline"
              className="mt-4 border-[var(--c-border)] text-[var(--c-muted)] hover:text-[var(--c-text)] hover:bg-[var(--c-surface-2)] rounded-sm"
              onClick={() => setTeam((ts) => [...ts, { name: "", email: "", role: "engineer" }])}>
              <Plus size={14} className="mr-1.5" /> Add member
            </Button>
          </div>
        )}

        <div className="flex items-center justify-between mt-12 pt-6 border-t border-[var(--c-border)]">
          <Button data-testid="onboarding-back-btn" variant="outline" disabled={step === 0}
            onClick={() => setStep((s) => s - 1)}
            className="border-[var(--c-border)] text-[var(--c-muted)] hover:text-[var(--c-text)] hover:bg-[var(--c-surface-2)] rounded-sm">
            <ArrowLeft size={14} className="mr-1.5" /> Back
          </Button>
          <div className="text-[10px] font-mono tracking-widest text-[var(--c-muted)] uppercase">
            {industries.length} industries · {modules.length} modules · {stages.length} stages
          </div>
          {step < 3 ? (
            <Button data-testid="onboarding-continue-btn" onClick={() => setStep((s) => s + 1)}
              disabled={step === 0 && industries.length === 0}
              className="bg-[var(--c-accent)] hover:bg-[var(--c-accent-hover)] text-white rounded-sm">
              Continue <ArrowRight size={14} className="ml-1.5" />
            </Button>
          ) : (
            <Button data-testid="onboarding-complete-btn" onClick={complete} disabled={busy}
              className="bg-[var(--c-green)] hover:bg-[var(--c-green)]/85 text-white rounded-sm">
              {busy ? "Setting up…" : "Complete Setup"} <Check size={14} className="ml-1.5" weight="bold" />
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}
